import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Badge, Table, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getVisiblePages } from '../../utils/pagination';
import TableCard from '../../components/common/TableCard';
import ConfirmModal from '../../components/common/ConfirmModal';
import AddUnitsModal from '../../components/Configs/AddUnitsModal';
import EditUnitsModal from '../../components/Configs/EditUnitsModal';
import { getUnitMeasures, deleteUnitMeasure } from '../../services/Other/GeneralQueries';

function ItemUnits() {
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);

    const [showAddUnitMeasuresModal, setShowAddUnitMeasuresModal] = useState(false);
    const [showEditUnitMeasuresModal, setShowEditUnitMeasuresModal] = useState(false);
    const [selectedUnitMeasuresId, setSelectedUnitMeasuresId] = useState(0);

    const [reload, setReload] = useState(false);
    const [rows, setRows] = useState([]);

    // paginación
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // filtros
    const [filters, setFilters] = useState({ search: '', isActive: '' });
    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    // confirm delete
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedFilters(filters), 350);
        return () => clearTimeout(t);
    }, [filters]);

    const fetchUnits = async () => {
        try {
            if (rows.length === 0) setLoading(true);
            else setFetching(true);

            const list = await getUnitMeasures();
            setRows(Array.isArray(list) ? list : []);
        } catch (e) {
            toast.error('Error al cargar unidades de medida');
            setRows([]);
        } finally {
            setLoading(false);
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchUnits();
    }, [reload]);

    // Filtrado
    const filteredRows = useMemo(() => {
        const q = (debouncedFilters.search || '').trim().toLowerCase();
        const status = debouncedFilters.isActive;

        return (rows || []).filter((r) => {
            const matchesSearch =
                !q ||
                (r.name || '').toLowerCase().includes(q) ||
                (r.abbreviation || '').toLowerCase().includes(q);

            const matchesStatus =
                !status ||
                (status === 'true' ? r.isActive === true : r.isActive === false);

            return matchesSearch && matchesStatus;
        });
    }, [rows, debouncedFilters]);

    // Paginación
    const totalRows = filteredRows.length;
    const safePageCount = Math.ceil(totalRows / limit);
    const currentPage = safePageCount === 0 ? 0 : Math.min(page, safePageCount);

    const paginatedRows = useMemo(() => {
        if (safePageCount === 0) return [];
        const start = (currentPage - 1) * limit;
        return filteredRows.slice(start, start + limit);
    }, [filteredRows, currentPage, limit, safePageCount]);

    const visiblePages = getVisiblePages(currentPage, safePageCount);

    useEffect(() => {
        setPage(1);
    }, [debouncedFilters, limit]);

    const handleOpenEditModal = (id) => {
        setSelectedUnitMeasuresId(id);
        setShowEditUnitMeasuresModal(true);
    };

    const askDelete = (id) => {
        setDeleteId(id);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteId) return;

        setDeleting(true);
        try {
            const res = await deleteUnitMeasure(deleteId);
            const success = res?.result === true || res === true;

            if (success) {
                toast.success('Unidad de medida eliminada');
                setReload((s) => !s);
                setShowConfirmDelete(false);
                setDeleteId(null);
            } else {
                toast.error('No se pudo eliminar');
            }
        } catch (e) {
            toast.error('Error al eliminar');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h2 className="mb-0">Unidades de Medida</h2>
                <Button variant="primary" onClick={() => setShowAddUnitMeasuresModal(true)}>
                    Agregar
                </Button>
            </div>

            {/* Filtros */}
            <Row className="g-3 align-items-end mb-3">
                <Col md={3}>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                        value={filters.isActive}
                        onChange={(e) => setFilters((s) => ({ ...s, isActive: e.target.value }))}
                    >
                        <option value="">Todos</option>
                        <option value="true">Activas</option>
                        <option value="false">Inactivas</option>
                    </Form.Select>
                </Col>

                <Col md={6}>
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control
                        value={filters.search}
                        placeholder="Buscar por nombre o abreviación..."
                        onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))}
                    />
                </Col>

                <Col md={3}>
                    <Form.Label>Por página</Form.Label>
                    <Form.Select
                        value={limit}
                        onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                        title="Registros por página"
                    >
                        {[10, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            {/* Tabla */}
            <TableCard>
                <Table bordered hover responsive className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th className="actions-col">Acciones</th>
                            <th>Nombre</th>
                            <th>Abreviación</th>
                            <th>Estado</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={4} className="text-center py-5">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Cargando...
                                </td>
                            </tr>
                        ) : paginatedRows.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center py-5">Sin registros</td>
                            </tr>
                        ) : (
                            paginatedRows.map((row) => (
                                <tr key={row.id}>
                                    <td className="actions-col">
                                        <div className="actions-cell">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleOpenEditModal(row.id)}>
                                                Editar
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => askDelete(row.id)}>
                                                Eliminar
                                            </Button>
                                        </div>
                                    </td>

                                    <td>{row.name}</td>
                                    <td className="col-nowrap">{row.abbreviation}</td>
                                    <td>
                                        {row.isActive ? <Badge bg="success">Activa</Badge> : <Badge bg="secondary">Inactiva</Badge>}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </TableCard>

            {/* Indicador de actualización */}
            {fetching && <div className="small text-muted mt-2">Actualizando...</div>}

            {/* Footer paginación */}
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted">
                    Página {currentPage} de {safePageCount} ({totalRows} registros)
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage(1)}
                        disabled={safePageCount === 0 || currentPage <= 1}
                    >
                        «
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePageCount === 0 || currentPage <= 1}
                    >
                        Anterior
                    </Button>

                    {visiblePages.length > 0 && visiblePages[0] > 1 && (
                        <>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setPage(1)}
                            >
                                1
                            </Button>
                            {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
                        </>
                    )}

                    {visiblePages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size="sm"
                            variant={pageNumber === currentPage ? 'secondary' : 'outline-secondary'}
                            onClick={() => setPage(pageNumber)}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {visiblePages.length > 0 && visiblePages[visiblePages.length - 1] < safePageCount && (
                        <>
                            {visiblePages[visiblePages.length - 1] < safePageCount - 1 && (
                                <span className="px-1 text-muted">…</span>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setPage(safePageCount)}
                            >
                                {safePageCount}
                            </Button>
                        </>
                    )}

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(safePageCount, p + 1))}
                        disabled={safePageCount === 0 || currentPage >= safePageCount}
                    >
                        Siguiente
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage(safePageCount)}
                        disabled={safePageCount === 0 || currentPage >= safePageCount}
                    >
                        »
                    </Button>
                </div>
            </div>

            {/* Modales */}
            <AddUnitsModal
                show={showAddUnitMeasuresModal}
                handleClose={() => setShowAddUnitMeasuresModal(false)}
                handleReload={() => setReload((s) => !s)}
            />

            <EditUnitsModal
                unitMeasuresId={selectedUnitMeasuresId}
                show={showEditUnitMeasuresModal}
                handleClose={() => setShowEditUnitMeasuresModal(false)}
                handleReload={() => setReload((s) => !s)}
            />

            {/* Confirm delete */}
            <ConfirmModal
                show={showConfirmDelete}
                onHide={() => {
                    if (deleting) return;
                    setShowConfirmDelete(false);
                    setDeleteId(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Eliminar unidad de medida"
                message="¿Deseas eliminar esta unidad de medida?"
                confirmText="Eliminar"
                confirmVariant="danger"
                denyText={null}
                cancelText="Cancelar"
                isLoading={deleting}
            />
        </>
    );
}

export default ItemUnits;